import { useMemo } from 'react';
import cn from 'classnames';
import { cssX, markMatch } from '@datassential/platform-ui-lib';
import { sanitizeForSearch } from '@helpers';
import { TerritoryOption } from '@apiHooks/useTerritoryOptions';
import { CustomBaseScrollbars } from '@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList';
import InputField from '@components/new/InputField/InputField';
import { IconSearchWhite } from '@icons';

const VISIBLE_HEIGHT_PX = 272;

interface TerritoryPillGridProps {
    options: TerritoryOption[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    search: string;
    onSearchChange: (value: string) => void;
}

const TerritoryPillGrid = ({ options, selectedIds, onChange, search, onSearchChange }: TerritoryPillGridProps) => {
    const trimmedSearch = search.trim();
    const isSearching = trimmedSearch !== '';

    const filteredOptions = useMemo(() => {
        if (!isSearching) return options;
        const term = trimmedSearch.toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(term));
    }, [options, isSearching, trimmedSearch]);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const handleToggle = (id: string) => {
        if (selectedIdSet.has(id)) {
            onChange(selectedIds.filter(s => s !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const handleSelectAll = () => {
        onChange(Array.from(new Set([...selectedIds, ...filteredOptions.map(o => o.id)])));
    };

    const handleDeselectAll = () => {
        const filteredIdSet = new Set(filteredOptions.map(o => o.id));
        onChange(selectedIds.filter(id => !filteredIdSet.has(id)));
    };

    const hasResults = filteredOptions.length > 0;

    return (
        <div className="TerritoryPillGrid">
            <div className="TerritoryPillGrid__controls">
                <div className="TerritoryPillGrid__search">
                    <InputField
                        value={search}
                        onChange={value => onSearchChange(sanitizeForSearch(value))}
                        placeholder="Search"
                        style={{
                            input: {
                                paddingLeft: 28,
                                paddingTop: 0,
                                paddingBottom: 10,
                            },
                        }}
                    >
                        <IconSearchWhite
                            style={{
                                position: 'absolute',
                                top: 4,
                                left: 0,
                                opacity: 0.6,
                            }}
                        />
                    </InputField>
                </div>
                <div className="TerritoryPillGrid__bulk-actions">
                    <button
                        type="button"
                        className="TerritoryPillGrid__bulk-btn"
                        onClick={handleSelectAll}
                    >
                        select all
                    </button>
                    <span className="TerritoryPillGrid__bulk-sep">|</span>
                    <button
                        type="button"
                        className="TerritoryPillGrid__bulk-btn"
                        onClick={handleDeselectAll}
                    >
                        deselect all
                    </button>
                </div>
            </div>
            <div className="TerritoryPillGrid__list">
                {!hasResults && isSearching ? (
                    <div className={cn('TerritoryPillGrid__no-results', cssX('SearchBox__no-results'))}>
                        Nothing found.<br />Try searching something else.
                    </div>
                ) : (
                    <CustomBaseScrollbars autoHeight autoHeightMax={VISIBLE_HEIGHT_PX}>
                        <div className="TerritoryPillGrid__grid">
                            {filteredOptions.map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={cn('TerritoryPillGrid__pill', { 'is-selected': selectedIdSet.has(option.id) })}
                                    aria-pressed={selectedIdSet.has(option.id)}
                                    aria-label={option.label}
                                    onClick={() => handleToggle(option.id)}
                                    dangerouslySetInnerHTML={{
                                        __html: markMatch(option.label, trimmedSearch),
                                    }}
                                />
                            ))}
                        </div>
                    </CustomBaseScrollbars>
                )}
            </div>
        </div>
    );
};

export default TerritoryPillGrid;
